import { forwardRef, useId } from 'react';
import type { InputProps } from './Input.types';
import './Input.scss';

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, iconLeft, iconRight, actionRight, required, id, className, type = 'text', ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id || `input-${autoId}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  const fieldClasses = [
    'input__field',
    iconLeft ? 'input__field--with-icon-left' : '',
    iconRight || actionRight ? 'input__field--with-icon-right' : '',
    error ? 'input__field--error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={['input', className || ''].filter(Boolean).join(' ')}>
      {label ? (
        <label className="input__label" htmlFor={inputId}>
          {label}
          {required ? <span className="input__required" aria-hidden="true">*</span> : null}
        </label>
      ) : null}
      <div className="input__wrap">
        {iconLeft ? <span className="input__icon input__icon--left" aria-hidden="true">{iconLeft}</span> : null}
        <input
          ref={ref}
          id={inputId}
          type={type}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          className={fieldClasses}
          {...rest}
        />
        {actionRight ? (
          <span className="input__action" aria-hidden="true">{actionRight}</span>
        ) : iconRight ? (
          <span className="input__icon input__icon--right" aria-hidden="true">{iconRight}</span>
        ) : null}
      </div>
      {error ? (
        <span id={`${inputId}-error`} className="input__error" role="alert">{error}</span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className="input__hint">{hint}</span>
      ) : null}
    </div>
  );
});

export default Input;
